import React, { useState, useEffect } from 'react';
import { Modal, Checkbox } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import { Button } from 'components/Button';
import confirmModalPic from '../../../Assets/userManagementV3/confirmModalPic.png';

import './userActionConfirmModal.css';

const ACTION_CONFIG = {
    suspend: {
        title: 'Confirm',
        descriptionSingle:
            'Once you confirm, the user will be logged out from all devices.',
        descriptionBulk:
            'Once you confirm, the users will be logged out from all devices.',

        confirmTextSingle: 'YES, SUSPEND USER',
        confirmTextBulk: 'YES, SUSPEND USERS',
        loadingTextSingle: 'SUSPENDING...',
        loadingTextBulk: 'SUSPENDING...',

        confirmClass: 'suspend-confirm-btn',

        showCheckbox: false,
    },

    activate: {
        title: 'Confirm',
        descriptionSingle:
            'Once you confirm, the user will regain access and be able to log in again.',
        descriptionBulk:
            'Once you confirm, the users will regain access and be able to log in again.',

        confirmTextSingle: 'YES, ACTIVATE USER',
        confirmTextBulk: 'YES, ACTIVATE USERS',
        loadingTextSingle: 'ACTIVATING...',
        loadingTextBulk: 'ACTIVATING...',

        confirmClass: 'activate-confirm-btn',

        showCheckbox: false,
    },

    delete: {
        title: 'Confirm',

        warningSingle:
            'You are about to permanently delete user',

        warningBulk:
            'You are about to permanently delete users',

        descriptionSingle:
            'The user will be permanently deleted from the database along with their data. You cannot undo this action.',

        descriptionBulk:
            'The users will be permanently deleted from the database along with their data. You cannot undo this action.',

        confirmTextSingle: 'YES, DELETE USER',
        confirmTextBulk: 'YES, DELETE USERS',
        loadingTextSingle: 'DELETING...',
        loadingTextBulk: 'DELETING...',

        confirmClass: 'delete-confirm-btn',

        showCheckbox: true,

        checkboxTextSingle:
            'I understand that deleting user will remove them permanently from the system.',
        checkboxTextBulk:
            'I understand that deleting users will remove them permanently from the system.',
        checkboxText:
            'I understand that deleting users will remove them permanently from the system.',
    },
    accessDelete: {
        title: 'Confirm',
        descriptionSingle:
            'Are you sure you want to delete the access?',
        descriptionBulk:
            'Are you sure you want to delete the access?',

        confirmTextSingle: 'YES, DELETE',
        confirmTextBulk: 'YES, DELETE',
        loadingTextSingle: 'DELETING...',
        loadingTextBulk: 'DELETING...',

        confirmClass: 'suspend-confirm-btn',

        showCheckbox: false,
    },

    save: {
        title: 'Confirm',

        descriptionSingle:
            'Are you sure you want to save the changes ?',

        confirmTextSingle: 'YES',
        loadingTextSingle: 'SAVING...',

        confirmClass: 'save-confirm-btn',

        showCheckbox: false,
    },

    deviceReject: {
        title: 'Confirm',

        descriptionSingle:
            'Are you sure you want to reject?',

        confirmTextSingle: 'REJECT',
        loadingTextSingle: 'REJECTING...',

        confirmClass: 'suspend-confirm-btn',

        showCheckbox: false,
        cancelFirst: true,
    },

    deviceApprove: {
        title: 'Confirm',

        descriptionSingle:
            'Are you sure you want to approve?',

        confirmTextSingle: 'APPROVE',
        loadingTextSingle: 'APPROVING...',

        confirmClass: 'activate-confirm-btn',

        showCheckbox: false,
        cancelFirst: true,
    },

    documentDelete: {
        title: 'Confirm',

        descriptionSingle:
            'Are you sure you want to delete?',

        confirmTextSingle: 'DELETE',
        loadingTextSingle: 'DELETING...',

        confirmClass: 'suspend-confirm-btn',

        showCheckbox: false,
        cancelFirst: true,
    },
};

const UserActionConfirmModal = ({
    open,
    onCancel,
    onConfirm,

    action = 'suspend',

    // true = multiple users
    // false = one user
    isBulk = false,

    loading = false,
}) => {
    const [isAcknowledged, setIsAcknowledged] = useState(false);

    const config = ACTION_CONFIG[action] || ACTION_CONFIG.suspend;

    useEffect(() => {
        if (!open) {
            setIsAcknowledged(false);
        }
    }, [open]);

    const isDelete = action === 'delete';

    const getWarningText = () => {
        if (!isDelete) {
            return null;
        }

        return isBulk
            ? config.warningBulk
            : 'You are about to permanently delete user';
    };

    const getDescription = () => {
        if (action === 'suspend' || action === 'activate') {
            return isBulk
                ? config.descriptionBulk
                : config.descriptionSingle;
        }

        if (action === 'delete') {
            return isBulk
                ? config.descriptionBulk
                : config.descriptionSingle;
        }

        return config.descriptionSingle;
    };

    const getConfirmText = () => {
        if (loading) {
            return isBulk
                ? (config.loadingTextBulk || 'LOADING...')
                : (config.loadingTextSingle || 'LOADING...');
        }

        if (isBulk) {
            return config.confirmTextBulk;
        }

        return config.confirmTextSingle;
    };

    const handleConfirm = () => {
        if (loading) {
            return;
        }
        if (isDelete && !isAcknowledged) {
            return;
        }

        onConfirm?.();
    };

    return (
        <Modal
            className="user-action-confirm-modal"
            centered
            visible={open}
            onCancel={loading ? undefined : onCancel}
            footer={null}
            // width={520}
            closable={!loading}
            maskClosable={!loading}
            closeIcon={ <CloseOutlined className="user-action-confirm-close" /> }
        >
            <div className="user-action-confirm-content">

                {/* IMAGE */}
                <div className="user-action-confirm-image-wrapper">
                    <img
                        src={confirmModalPic}
                        alt="Confirm"
                        className="user-action-confirm-image"
                    />
                </div>

                {/* TITLE */}
                <div className="user-action-confirm-title">
                    {config.title}
                </div>

                {/* DELETE WARNING */}
                {getWarningText() && (
                    <div className="user-action-confirm-warning">
                        {getWarningText()}
                    </div>
                )}

                {/* DESCRIPTION */}
                <div
                    className={`
                        user-action-confirm-description
                        ${isDelete
                            ? 'delete-description'
                            : ''
                        }
                    `}
                >
                    {getDescription()}
                </div>

                {/* DELETE CHECKBOX */}
                {config.showCheckbox && (
                    <div className="user-action-confirm-checkbox-wrapper">
                        <Checkbox
                            checked={isAcknowledged}
                            disabled={loading}
                            onChange={(e) =>
                                !loading && setIsAcknowledged(e.target.checked)
                            }
                        />

                        <span>
                            {isBulk
                                ? (config.checkboxTextBulk || config.checkboxText)
                                : (config.checkboxTextSingle || config.checkboxText)}
                        </span>
                    </div>
                )}

                {/* BUTTONS */}
                <div
                    className="user-action-confirm-actions"
                    style={config.cancelFirst ? { flexDirection: 'row-reverse' } : undefined}
                >
                    <button
                        type="button"
                        disabled={
                            loading ||
                            (isDelete &&
                            !isAcknowledged)
                        }
                        className={`
                            user-action-confirm-btn
                            ${config.confirmClass}
                        `}
                        style={loading ? { opacity: 0.8, cursor: 'not-allowed' } : undefined}
                        onClick={handleConfirm}
                    >
                        {getConfirmText()}
                    </button>

                    <button
                        type="button"
                        className="user-action-cancel-btn"
                        disabled={loading}
                        style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                        onClick={loading ? undefined : onCancel}
                    >
                        CANCEL
                    </button>

                </div>

            </div>
        </Modal>
    );
};

export default UserActionConfirmModal;